import { Component } from '@theme/component';

/** @param {Event} event */
const preventDefault = (event) => event.preventDefault();

/**
 * Lightweight custom element that wraps the two images of a resource card and
 * wires up prev/next keyboard navigation via the Component event framework.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} [previous] - The previous button.
 * @property {HTMLButtonElement} [next] - The next button.
 *
 * @extends {Component<Refs>}
 */
class ResourceCardMedia extends Component {
  /** @type {0 | 1} */
  #index = 0;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#syncButtons();

    // Prevent the buttons from stealing focus on mouse click so that parent
    // components relying on blur/focus events (e.g. header-menu) are not
    // triggered when an arrow is clicked. Keyboard Tab-focus is unaffected.
    const opts = { signal: this.#abortController.signal };
    this.refs.previous?.addEventListener('mousedown', preventDefault, opts);
    this.refs.next?.addEventListener('mousedown', preventDefault, opts);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  previous() {
    if (this.#index === 0) return;
    this.#index = 0;
    this.dataset.index = '0';
    this.#syncButtons();
    this.#focusCard();
  }

  next() {
    if (this.#index === 1) return;
    this.#index = 1;
    this.dataset.index = '1';
    this.#syncButtons();
    this.#focusCard();
  }

  #syncButtons() {
    if (this.refs.previous) this.refs.previous.disabled = this.#index === 0;
    if (this.refs.next) this.refs.next.disabled = this.#index === 1;
  }

  #focusCard() {
    this.closest('.resource-card')?.querySelector('.resource-card__link')?.focus();
  }
}

if (!customElements.get('resource-card-media')) {
  customElements.define('resource-card-media', ResourceCardMedia);
}
